import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Link, router } from '@inertiajs/react'
import {
  Edit,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Users,
  Award,
  Flame
} from 'lucide-react'

interface Answer {
  text: string
}

interface Quiz {
  id: number
  title: string
  question: string
  answers: Answer[]
  correct_answer_indices: number[]
  category: string
  category_label: string
  difficulty_level: number
  points_reward: number
  week_start_date: string
  week_end_date: string
  week_range: string
  published: boolean
  active: boolean
  total_submissions: number
  correct_submissions: number
  success_rate: number
  community_id: number
  community_name: string
  created_at: string
  updated_at: string
}

interface Submission {
  id: number
  user_name: string
  user_id: number
  selected_answer_indices: number[]
  selected_answer_texts: string[]
  is_correct: boolean
  points_earned: number
  streak_bonus_applied: boolean
  streak_count: number
  created_at: string
}

interface Stats {
  total_submissions: number
  correct_submissions: number
  incorrect_submissions: number
  success_rate: number
  average_points: number
  streak_bonuses: number
}

interface ShowProps {
  quiz: Quiz
  submissions: Submission[]
  stats: Stats
}

const categoryColors: Record<string, string> = {
  sav: 'bg-blue-100 text-blue-800',
  discours: 'bg-purple-100 text-purple-800',
  pratiques: 'bg-green-100 text-green-800'
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Show({ quiz, submissions, stats }: ShowProps) {
  const handlePublish = () => {
    if (confirm('Voulez-vous publier ce quiz ?')) {
      router.post(`/admin/weekly_quizzes/${quiz.id}/publish`)
    }
  }

  const handleUnpublish = () => {
    if (confirm('Voulez-vous dépublier ce quiz ?')) {
      router.post(`/admin/weekly_quizzes/${quiz.id}/unpublish`)
    }
  }

  const handleDelete = () => {
    if (confirm(`Voulez-vous vraiment supprimer ce quiz ?\n"${quiz.title}"\n\nCette action est irréversible.`)) {
      router.delete(`/admin/weekly_quizzes/${quiz.id}`)
    }
  }

  return (
    <AdminLayout
      title={quiz.title}
      breadcrumbs={[
        { label: 'Quiz Hebdomadaires', href: '/admin/weekly_quizzes' },
        { label: quiz.title }
      ]}
    >
      {/* Header Card */}
      <Card className="p-6 mb-6">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-text-dark mb-3">{quiz.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Badge className={categoryColors[quiz.category] || 'bg-gray-100 text-gray-800'}>
                {quiz.category_label}
              </Badge>
              <span className="text-sm text-text-gray">
                {'⭐'.repeat(quiz.difficulty_level)} Difficulté {quiz.difficulty_level}
              </span>
              <span className="text-sm text-text-gray">{quiz.week_range}</span>
              <span className="text-sm text-text-gray">{quiz.community_name}</span>
            </div>
            <div className="flex items-center gap-2">
              {quiz.published ? (
                <Badge className="bg-green-100 text-green-800">Publié</Badge>
              ) : (
                <Badge className="bg-gray-100 text-gray-800">Brouillon</Badge>
              )}
              {quiz.active && (
                <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  Actif cette semaine
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {quiz.published ? (
              <Button variant="secondary" onClick={handleUnpublish} className="flex items-center gap-2">
                <EyeOff size={18} />
                Dépublier
              </Button>
            ) : (
              <Button onClick={handlePublish} className="flex items-center gap-2">
                <Eye size={18} />
                Publier
              </Button>
            )}
            <Link href={`/admin/weekly_quizzes/${quiz.id}/edit`}>
              <Button variant="secondary" className="flex items-center gap-2">
                <Edit size={18} />
                Modifier
              </Button>
            </Link>
            <Button
              variant="secondary"
              className="text-red-600 hover:bg-red-50"
              onClick={handleDelete}
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      </Card>

      {/* Question and Answers */}
      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-dark mb-4">Question et Réponses</h2>
        <div className="mb-6">
          <p className="text-lg text-text-dark font-medium mb-4">{quiz.question}</p>
        </div>
        {quiz.correct_answer_indices.length > 1 && (
          <p className="text-sm text-blue-600 font-medium mb-3">
            Quiz à choix multiples ({quiz.correct_answer_indices.length} bonnes réponses)
          </p>
        )}
        <div className="space-y-2">
          {quiz.answers.map((answer, index) => {
            const isCorrect = quiz.correct_answer_indices.includes(index)
            return (
              <div
                key={index}
                className={`flex items-center gap-3 p-3 rounded border ${
                  isCorrect
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                {isCorrect ? (
                  <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
                ) : (
                  <XCircle size={20} className="text-gray-400 flex-shrink-0" />
                )}
                <span className={`flex-1 ${
                  isCorrect ? 'font-medium text-green-900' : 'text-text-dark'
                }`}>
                  {answer.text}
                </span>
                {isCorrect && (
                  <Badge className="bg-green-600 text-white">Bonne réponse</Badge>
                )}
              </div>
            )
          })}
        </div>
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm text-blue-900">
            <strong>Points :</strong> {quiz.points_reward} points pour une bonne réponse, +10 points bonus au 3e bonne réponse consécutive
          </p>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users size={18} className="text-gray-400" />
            <p className="text-sm text-text-gray">Total</p>
          </div>
          <p className="text-2xl font-bold text-text-dark">{stats.total_submissions}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 size={18} className="text-green-600" />
            <p className="text-sm text-text-gray">Correctes</p>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.correct_submissions}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <XCircle size={18} className="text-red-600" />
            <p className="text-sm text-text-gray">Incorrectes</p>
          </div>
          <p className="text-2xl font-bold text-red-600">{stats.incorrect_submissions}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={18} className="text-blue-600" />
            <p className="text-sm text-text-gray">Taux réussite</p>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.success_rate}%</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Award size={18} className="text-yellow-600" />
            <p className="text-sm text-text-gray">Pts moyens</p>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.average_points}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Flame size={18} className="text-orange-600" />
            <p className="text-sm text-text-gray">Bonus streak</p>
          </div>
          <p className="text-2xl font-bold text-orange-600">{stats.streak_bonuses}</p>
        </Card>
      </div>

      {/* Submissions */}
      <Card className="overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-text-dark">
            Soumissions récentes ({submissions.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Utilisateur</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Réponse sélectionnée</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Résultat</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Points</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Streak</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {submissions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-text-gray">
                    Aucune soumission pour le moment
                  </td>
                </tr>
              ) : (
                submissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link
                        href={`/admin/users/${submission.user_id}`}
                        className="font-medium text-tessan-primary hover:underline"
                      >
                        {submission.user_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-text-dark max-w-xs truncate">
                        {submission.selected_answer_texts.join(', ')}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {submission.is_correct ? (
                        <Badge className="bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                          <CheckCircle2 size={14} />
                          Correct
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                          <XCircle size={14} />
                          Incorrect
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-text-dark">
                        +{submission.points_earned} pts
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {submission.streak_bonus_applied && (
                          <Flame size={16} className="text-orange-600" title="Bonus streak appliqué" />
                        )}
                        <span className="text-sm text-text-gray">
                          {submission.streak_count}x
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {formatDateTime(submission.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  )
}
